import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

const DEFAULT_LEFT_PX = 280;
const MIN_LEFT_PX = 180;
const MIN_RIGHT_PX = 240;

export interface AdminOrchSplitPaneProps {
  left: ReactNode;
  right: ReactNode;
  /** Initial left pane width in px or as a percentage of the available span. */
  defaultLeftWidth?: number | `${number}%`;
  className?: string;
  /** Accessible name for the resize handle. */
  resizeLabel?: string;
}

/**
 * Horizontal master/detail layout with a draggable vertical separator.
 */
export default function AdminOrchSplitPane({
  left,
  right,
  defaultLeftWidth = DEFAULT_LEFT_PX,
  className = '',
  resizeLabel = 'Resize panels',
}: AdminOrchSplitPaneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [dragging, setDragging] = useState(false);

  const widthInPixels = useCallback((width: number | `${number}%`, containerWidth: number) => {
    if (typeof width === 'number') return width;
    return containerWidth * (Number.parseFloat(width) / 100);
  }, []);

  const clampWidth = useCallback((raw: number, containerWidth: number) => {
    const maxLeft = Math.max(MIN_LEFT_PX, containerWidth - MIN_RIGHT_PX);
    return Math.min(Math.max(raw, MIN_LEFT_PX), maxLeft);
  }, []);

  useEffect(() => {
    if (!dragging) return;

    const onMove = (event: PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const next = clampWidth(event.clientX - rect.left, rect.width);
      setLeftWidth(next);
    };

    const onUp = () => setDragging(false);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [dragging, clampWidth]);

  const onHandleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el) return;
    const step = event.shiftKey ? 40 : 16;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setLeftWidth((w) => {
        const containerWidth = el.getBoundingClientRect().width;
        return clampWidth(widthInPixels(w, containerWidth) - step, containerWidth);
      });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setLeftWidth((w) => {
        const containerWidth = el.getBoundingClientRect().width;
        return clampWidth(widthInPixels(w, containerWidth) + step, containerWidth);
      });
    }
  };

  const style = {
    '--admin-orch-split-left': typeof leftWidth === 'number' ? `${leftWidth}px` : leftWidth,
  } as CSSProperties;
  const ariaWidth =
    typeof leftWidth === 'number'
      ? leftWidth
      : widthInPixels(leftWidth, containerRef.current?.getBoundingClientRect().width || 0);

  return (
    <div
      ref={containerRef}
      className={`admin-orch-split${dragging ? ' is-dragging' : ''}${className ? ` ${className}` : ''}`}
      style={style}
    >
      <div className="admin-orch-split-left">{left}</div>
      <div
        className="admin-orch-split-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label={resizeLabel}
        aria-valuenow={Math.round(ariaWidth)}
        tabIndex={0}
        onPointerDown={(event) => {
          event.preventDefault();
          (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
          setDragging(true);
        }}
        onKeyDown={onHandleKeyDown}
      />
      <div className="admin-orch-split-right">{right}</div>
    </div>
  );
}
